import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { v4 as uuid } from 'uuid';

import type { Place } from '../../model/Place';
import type { SearchFilters } from '../../model/SearchFilters';
import type { Country } from '../../services/Country';
import { computeDateRange, DateTimeRangeSide, getNextDateTimeRangeSideInFlow } from '../../services/DateRange';
import { type Datelike, toISOString } from '../../services/DateTime';
import { LocationPrediction, type Prediction } from '../../services/LocationPrediction';
import { Logger } from '../../services/Logger';
import Tracking from '../../services/Tracking';
import { flashMessageError } from '../../store/session';
import Button from '../Button';
import DateTimeInput from '../Form/DateTimeInput';
import Loader from '../Loader';
import T, { withTranslation, type WithTranslationProps } from '../Translate';

import styles from './searchBar.module.scss';

interface Props {
  location?: Place;
  dateFrom?: Datelike;
  dateTo?: Datelike;
  className?: string;

  onSubmit(filters: Partial<SearchFilters>): void;
}

interface State {
  opened: DateTimeRangeSide;
  location: string;
  place?: Place;
  dateFrom: Datelike;
  dateTo: Datelike;
  predictions: Prediction[];
  predictionsLoading: boolean;
  boundsFrom: [Datelike, Datelike];
  boundsTo: [Datelike, Datelike];
}

class SearchBar extends React.Component<Props, State> {
  props: Props & WithTranslationProps & { dispatch: DispatchFunction; region: Country };

  private selectedSides = {
    [DateTimeRangeSide.LOCATION]: !!this.props.location?.name,
    [DateTimeRangeSide.DATE_FROM]: !!this.props.dateFrom,
    [DateTimeRangeSide.TIME_FROM]: !!this.props.dateFrom,
    [DateTimeRangeSide.DATE_TO]: !!this.props.dateTo,
    [DateTimeRangeSide.TIME_TO]: !!this.props.dateTo,
  };
  private predictionService = new LocationPrediction({
    language: this.props.translation.language,
    country: this.props.region,
  });
  private unlisten: () => void;
  private session = uuid();
  private predictionsTimeout = null;
  private ref = React.createRef<HTMLDivElement>();

  private get nextOpenedInFlow(): State['opened'] {
    return getNextDateTimeRangeSideInFlow(this.state.opened, this.selectedSides);
  }

  constructor(props: Props) {
    super(props);

    const { from, to, bounds_from, bounds_to } = computeDateRange(props.dateFrom, props.dateTo);
    this.state = {
      opened: DateTimeRangeSide.NONE,
      location: props.location?.name ?? '',
      dateFrom: from,
      dateTo: to,
      boundsFrom: bounds_from,
      boundsTo: bounds_to,
      predictions: [],
      predictionsLoading: false,
    };
    if (typeof window === 'undefined') return;
    this.unlisten = this.predictionService.onPrediction(predictions => {
      this.setState(prev => ({ ...prev, predictions }));
    });
    window.addEventListener('click', this.handleOutsideClick, true);
  }

  public componentWillUnmount() {
    this.unlisten?.();
    window.removeEventListener('click', this.handleOutsideClick);
  }

  private handleLocationChange(value: string) {
    if (this.state.location !== '' && value === '') {
      this.session = uuid();
    }
    const nextState: Partial<State> = {
      opened: DateTimeRangeSide.LOCATION,
      location: value,
    };
    if (value === '') {
      nextState.predictions = [];
    }
    if (this.predictionsTimeout) {
      clearTimeout(this.predictionsTimeout);
    }
    this.predictionsTimeout = setTimeout(() => {
      this.predictionService.query(value);
    }, 500);
    this.setState(prev => ({ ...prev, ...nextState }));
  }

  private handleLocationFocus() {
    this.setState(prev => ({ ...prev, opened: DateTimeRangeSide.LOCATION }));
  }

  private async handlePredictionSelect(prediction: Prediction) {
    this.setState(prev => ({
      ...prev,
      opened: DateTimeRangeSide.NONE,
      predictionsLoading: true,
      predictions: prediction.id === this.predictionService.geolocationPrediction.id ? [] : prev.predictions,
    }));
    try {
      const place = await prediction.getPlace();
      this.selectedSides[DateTimeRangeSide.LOCATION] = true;
      this.session = uuid();
      this.setState(prev => ({
        ...prev,
        location: place.name,
        place,
        predictionsLoading: false,
        opened: this.nextOpenedInFlow,
      }));
    } catch (error) {
      Logger.default.warning(`Geolocation error`, { error });
      this.selectedSides[DateTimeRangeSide.LOCATION] = false;
      this.props.dispatch(flashMessageError('searchBar.location.geolocationError'));
      this.setState(prev => ({
        ...prev,
        predictionsLoading: false,
        place: null,
        location: '',
        opened: DateTimeRangeSide.LOCATION,
        predictions: [],
      }));
    }
  }

  private handleDateFromChange = (value: Datelike) => this.handleDateChange(value, this.state.dateTo);
  private handleDateFromClick = () => this.handleDateClick(DateTimeRangeSide.DATE_FROM);
  private handleTimeFromClick = () => this.handleDateClick(DateTimeRangeSide.TIME_FROM);
  private handleDateToChange = (value: Datelike) => this.handleDateChange(this.state.dateFrom, value);
  private handleDateToClick = () => this.handleDateClick(DateTimeRangeSide.DATE_TO);
  private handleTimeToClick = () => this.handleDateClick(DateTimeRangeSide.TIME_TO);

  private computeNextStateForDateChange(dateFrom: Datelike, dateTo: Datelike, side: DateTimeRangeSide): Partial<State> {
    this.selectedSides[side] = true;
    const { from, to, bounds_from, bounds_to } = computeDateRange(dateFrom, dateTo);
    return {
      opened: this.nextOpenedInFlow,
      dateFrom: from,
      dateTo: to,
      boundsFrom: bounds_from,
      boundsTo: bounds_to,
    };
  }

  private handleDateChange(dateFrom: Datelike, dateTo: Datelike, side: DateTimeRangeSide = this.state.opened) {
    const nextState = this.computeNextStateForDateChange(dateFrom, dateTo, side);
    this.setState(prev => ({ ...prev, ...nextState }));
  }

  private handleDateClick(side: DateTimeRangeSide) {
    if (!this.state.opened) {
      this.setState(prev => ({ ...prev, opened: side }));
      return;
    }
    if (this.state.opened === side) {
      this.setState(prev => ({ ...prev, opened: DateTimeRangeSide.NONE }));
      return;
    }
    const nextState = this.computeNextStateForDateChange(this.state.dateFrom, this.state.dateTo, this.state.opened);
    nextState.opened = side;
    this.setState(prev => ({ ...prev, ...nextState }));
  }

  private handleSubmit = () => {
    const payload: Partial<SearchFilters> = {};
    if (this.selectedSides[DateTimeRangeSide.LOCATION]) {
      payload.location = this.state.place ?? {
        name: this.state.location,
      };
    }
    if (this.selectedSides[DateTimeRangeSide.DATE_FROM] || this.selectedSides[DateTimeRangeSide.DATE_TO]) {
      payload.date_from = toISOString(this.state.dateFrom);
      payload.date_to = toISOString(this.state.dateTo);
    }
    Tracking.track('SEARCH_SUBMITTED', payload);
    this.props.onSubmit(payload);
  };

  private handleOutsideClick = (event: MouseEvent) => {
    if (!this.state.opened || this.ref.current?.contains(event.target as Node)) return;
    if (this.state.opened) {
      this.setState(prev => ({ ...prev, opened: DateTimeRangeSide.NONE }));
    }
  };

  public render() {
    const { opened } = this.state;
    const { translate } = this.props.translation;

    return (
      <div className={classNames(styles.container, this.props.className)} ref={this.ref}>
        <div
          className={classNames(styles.locationContainer, {
            opened: opened === DateTimeRangeSide.LOCATION,
          })}
        >
          <label className={styles.input} htmlFor="location-input">
            <i className="icon icon-pin" />
            <div className={styles.floatingLabel}>
              <input
                autoComplete="off"
                id="location-input"
                onChange={e => this.handleLocationChange(e.target.value)}
                onFocus={() => this.handleLocationFocus()}
                placeholder={translate('global.searchBar.location.placeholder')}
                type="text"
                value={this.state.location}
              />
              <T as="span" id="global.searchBar.location.placeholder" />
            </div>
            {this.state.predictionsLoading ? <Loader className={styles.loader} /> : null}
          </label>
          <div className={styles.predictions}>
            <PredictionItem
              {...this.predictionService.geolocationPrediction}
              onClick={() => this.handlePredictionSelect(this.predictionService.geolocationPrediction)}
            />
            {this.state.predictions.map((prediction, index) => (
              <PredictionItem
                key={`${prediction.id}-${index}`}
                {...prediction}
                onClick={() => this.handlePredictionSelect(prediction)}
              />
            ))}
          </div>
        </div>
        <div className={styles.dates}>
          <DateTimeInput
            calendarTitle="global.searchBar.dateFrom.calendarTitle"
            dateOpened={opened === DateTimeRangeSide.DATE_FROM}
            datePlaceholder="global.searchBar.dateFrom.placeholder"
            label="global.searchBar.dateFrom.label"
            max={this.state.boundsFrom[1]}
            min={this.state.boundsFrom[0]}
            onChange={this.handleDateFromChange}
            onDateClick={this.handleDateFromClick}
            onTimeClick={this.handleTimeFromClick}
            range={{ from: this.state.dateFrom, to: this.state.dateTo }}
            selected={this.state.dateFrom}
            showDate={this.selectedSides[DateTimeRangeSide.DATE_FROM]}
            showTime={this.selectedSides[DateTimeRangeSide.TIME_FROM]}
            timeOpened={opened === DateTimeRangeSide.TIME_FROM}
            timePlaceholder="global.searchBar.timeFrom.placeholder"
          />
          <DateTimeInput
            calendarTitle="global.searchBar.dateTo.calendarTitle"
            dateOpened={opened === DateTimeRangeSide.DATE_TO}
            datePlaceholder="global.searchBar.dateTo.placeholder"
            label="global.searchBar.dateTo.label"
            max={this.state.boundsTo[1]}
            min={this.state.boundsTo[0]}
            onChange={this.handleDateToChange}
            onDateClick={this.handleDateToClick}
            onTimeClick={this.handleTimeToClick}
            range={{ from: this.state.dateFrom, to: this.state.dateTo }}
            selected={this.state.dateTo}
            showDate={this.selectedSides[DateTimeRangeSide.DATE_TO]}
            showTime={this.selectedSides[DateTimeRangeSide.TIME_TO]}
            timeOpened={opened === DateTimeRangeSide.TIME_TO}
            timePlaceholder="global.searchBar.timeTo.placeholder"
          />
        </div>
        <Button icon lg onClick={this.handleSubmit} type="submit" variant="secondary">
          <T id="global.searchBar.submit" />
          <i className="icon icon-search" />
        </Button>
      </div>
    );
  }
}

// @ts-expect-error TS2339: Property 'app' does not exist on type 'DefaultRootState'
export default connect(state => ({ region: state.app.region }))(withTranslation()(SearchBar));

interface PredictionItemProps extends Prediction {
  onClick(): void;
}

function PredictionItem(prediction: PredictionItemProps) {
  return (
    <button className={styles.prediction} onClick={prediction.onClick} type="button">
      <i className={classNames('icon', 'icon-' + (prediction.icon ?? 'pin'))} />
      {prediction.translate ? <T id={prediction.name} /> : <span>{prediction.name}</span>}
    </button>
  );
}
